import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Package, IndianRupee, Hash, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

interface Category {
  id: string
  name: string
}

interface QuickAddProductProps {
  isOpen: boolean
  onClose: () => void
  categories: Category[]
  onProductCreated: (product: any) => void
  formatCurrency: (amount: number) => string
}

export const QuickAddProduct = ({
  isOpen,
  onClose,
  categories,
  onProductCreated,
  formatCurrency
}: QuickAddProductProps) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category_id: '',
    stock_quantity: '1',
    gst_rate: '18',
    unit: 'pcs'
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        price: '',
        category_id: categories.length > 0 ? categories[0].id : '',
        stock_quantity: '1',
        gst_rate: '18',
        unit: 'pcs'
      })
    }
  }, [isOpen, categories])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Product name is required')
      return
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Valid price is required')
      return
    }

    setIsSubmitting(true)

    try {
      const productData = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        category_id: formData.category_id || null,
        stock_quantity: parseInt(formData.stock_quantity) || 1,
        min_stock_level: 0,
        gst_rate: parseFloat(formData.gst_rate),
        gst_type: 'inclusive',
        is_active: 1,
        sku: `CUSTOM-${Date.now()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`,
        unit: formData.unit,
        description: 'Quick add product from POS'
      }

      console.log('Creating product with data:', productData)

      // Call the parent callback with the product data
      await onProductCreated(productData)

      onClose()
      toast.success('Product created and added to cart!')
    } catch (error: any) {
      console.error('Failed to create product:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create product'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal/Overlay Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-0 pointer-events-none">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full lg:h-auto lg:max-w-md lg:max-h-[90vh] overflow-y-auto pointer-events-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg">
                <Plus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100">
                  Quick Add Product
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
                  Add product and add to cart
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4 lg:space-y-5">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Product Name *
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Custom Item"
                  className="pl-10 h-11 lg:h-12 text-base"
                  autoFocus
                />
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Price *
              </label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className="pl-10 h-11 lg:h-12 text-base"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                className="w-full h-11 lg:h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
                {categories.length === 0 && (
                  <option value="">No categories available</option>
                )}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Stock Quantity */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Stock Qty
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity: e.target.value }))}
                    placeholder="1"
                    className="pl-10 h-11 lg:h-12 text-base"
                  />
                </div>
              </div>

              {/* GST Rate */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  GST %
                </label>
                <select
                  value={formData.gst_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, gst_rate: e.target.value }))}
                  className="w-full h-11 lg:h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                >
                  <option value="0">0%</option>
                  <option value="5">5%</option>
                  <option value="12">12%</option>
                  <option value="18">18%</option>
                  <option value="28">28%</option>
                </select>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 lg:p-4">
              <p className="text-xs lg:text-sm text-blue-800 dark:text-blue-200">
                This product will be saved to your inventory and automatically added to the cart.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 h-11 lg:h-12 text-base"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 h-11 lg:h-12 text-base font-semibold bg-orange-600 hover:bg-orange-700"
              >
                {isSubmitting ? 'Adding...' : 'Add to Cart'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
